import * as Sentry from "@sentry/react";

export interface PaymentLogContext {
  merchantReference?: string;
  amount?: number | null | string;
  currency?: string;
  programId?: string;
  donationType?: string;
  paymentSessionId?: string;
  cardCountry?: string;
  customerEmail?: string;
  isRecurring?: boolean;
  isSpecialDonation?: boolean;
  isDedicated?: boolean;
  timestamp?: string;
  paymentType?: string;
  errorMessage?: string;
  formType?: string;
  paymentGateway?: string;
  donationId?: string;
  paymentMethod?: string;
  errorFields?: string[];
  responseCode?: number;
  isChallenge?: boolean;
  hasPaymentSessionId?: boolean;
  has3dsUrl?: boolean;
  redirectUrl?: string;
  recurringTransactionsCount?: number;
  recurringExpiryDate?: string;
  isDelivery?: boolean;
  isItemization?: boolean;
  cookies?: Record<string, string>;
  merchantSession?: string;
  applePayEvent?: string
}

export interface TokenizationLogContext {
  binInfo?: string;
  cardCountry?: string;
  merchantReference?: string;
  tokenizationAttempt?: boolean;
  validationErrors?: string[];
  timestamp?: string;
}

export enum PaymentEventType {
  PAYMENT_INITIATED = "payment_initiated",
  PAYMENT_PROCESSING_STARTED = "payment_processing_started",
  PAYMENT_GATEWAY_REQUEST = "payment_gateway_request",
  PAYMENT_GATEWAY_RESPONSE = "payment_gateway_response",
  PAYMENT_SUCCESS = "payment_success",
  PAYMENT_FAILED = "payment_failed",
  TOKENIZATION_STARTED = "tokenization_started",
  TOKENIZATION_SUCCESS = "tokenization_success",
  TOKENIZATION_FAILED = "tokenization_failed",
  SIGNATURE_GENERATION = "signature_generation",
  FORM_VALIDATION = "form_validation",
  API_CALL_STARTED = "api_call_started",
  API_CALL_SUCCESS = "api_call_success",
  API_CALL_FAILED = "api_call_failed",
  USER_REDIRECTED = "user_redirected",
  IP_FETCH_STARTED = "ip_fetch_started",
  IP_FETCH_SUCCESS = "ip_fetch_success",
  IP_FETCH_FAILED = "ip_fetch_failed",
  BROWSER_COOKIES = "browser_cookies",
}

export class PaymentLogger {
  /**
   * Log payment-related events with structured data
   */
  static logPaymentEvent(
    eventType: PaymentEventType,
    message: string,
    context: PaymentLogContext = {},
    level: "info" | "warning" | "error" = "info",
  ) {
    const enrichedContext = {
      ...context,
      timestamp: new Date().toISOString(),
      eventType,
      category: "payment",
    };

    // Set user context if available
    if (context.customerEmail) {
      Sentry.setUser({ email: context.customerEmail });
    }

    // Set transaction context
    Sentry.setTag("payment.event_type", eventType);
    Sentry.setTag("payment.currency", context.currency || "unknown");
    Sentry.setTag("payment.program_id", context.programId || "unknown");

    // Set financial context
    if (context.amount) {
      Sentry.setContext("financial", {
        amount: context.amount,
        currency: context.currency,
        isRecurring: context.isRecurring,
      });
    }

    // Set payment context
    Sentry.setContext("payment_details", enrichedContext);

    switch (level) {
      case "error":
        Sentry.captureException(new Error(message), {
          contexts: { payment_event: enrichedContext },
        });
        break;
      case "warning":
        Sentry.captureMessage(message, "warning");
        break;
      default:
        Sentry.addBreadcrumb({
          message,
          level,
          category: "payment",
          data: enrichedContext,
        });
        break;
    }

    // Also log to console in development
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log(`[PaymentLogger] ${eventType}: ${message}`, enrichedContext);
    }
  }

  /**
   * Log tokenization-specific events
   */
  static logTokenizationEvent(
    eventType: PaymentEventType,
    message: string,
    context: TokenizationLogContext = {},
    level: "info" | "warning" | "error" = "info",
  ) {
    const enrichedContext = {
      ...context,
      timestamp: new Date().toISOString(),
      eventType,
      category: "tokenization",
    };

    Sentry.setTag("tokenization.event_type", eventType);
    Sentry.setTag("tokenization.card_country", context.cardCountry || "unknown");

    Sentry.setContext("tokenization_details", enrichedContext);

    switch (level) {
      case "error":
        Sentry.captureException(new Error(message), {
          contexts: { tokenization_event: enrichedContext },
        });
        break;
      case "warning":
        Sentry.captureMessage(message, "warning");
        break;
      default:
        Sentry.addBreadcrumb({
          message,
          level,
          category: "tokenization",
          data: enrichedContext,
        });
        break;
    }

    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.log(`[TokenizationLogger] ${eventType}: ${message}`, enrichedContext);
    }
  }

  /**
   * Log form validation errors with context
   */
  static logValidationError(
    fieldName: string,
    error: string,
    formContext: Record<string, any> = {},
  ) {
    const sanitizedContext = {
      fieldName,
      error,
      formType: formContext.formType || "payment_form",
      timestamp: new Date().toISOString(),
      // Remove sensitive data
      sanitizedFormData: Object.keys(formContext).reduce(
        (acc, key) => {
          if (!["card_number", "card_security_code", "expiry_date"].includes(key)) {
            acc[key] = formContext[key];
          }
          return acc;
        },
        {} as Record<string, any>,
      ),
    };

    Sentry.setTag("validation.field", fieldName);
    Sentry.setContext("form_validation", sanitizedContext);

    Sentry.addBreadcrumb({
      message: `Form validation error in ${fieldName}: ${error}`,
      level: "warning",
      category: "form_validation",
      data: sanitizedContext,
    });
  }

  /**
   * Log API call performance and outcomes
   */
  static logApiCall(
    endpoint: string,
    method: string,
    status: "started" | "success" | "error",
    duration?: number,
    responseStatus?: number,
    errorMessage?: string,
  ) {
    const context = {
      endpoint,
      method,
      status,
      duration,
      responseStatus,
      errorMessage,
      timestamp: new Date().toISOString(),
    };

    Sentry.setTag("api.endpoint", endpoint);
    Sentry.setTag("api.method", method);
    Sentry.setTag("api.status", status);

    if (responseStatus) {
      Sentry.setTag("api.response_status", responseStatus.toString());
    }

    const message = `API ${method} ${endpoint} - ${status}${duration ? ` (${duration}ms)` : ""}`;

    if (status === "error") {
      Sentry.captureException(new Error(`API Error: ${errorMessage || "Unknown error"}`), {
        contexts: { api_call: context },
      });
    } else {
      Sentry.addBreadcrumb({
        message,
        level: "info",
        category: "api",
        data: context,
      });
    }
  }

  /**
   * Set user context for the payment session
   */
  static setUserContext(userInfo: {
    email?: string;
    id?: string;
    fullName?: string;
    phoneNumber?: string;
  }) {
    Sentry.setUser({
      id: userInfo.id,
      email: userInfo.email,
      username: userInfo.fullName,
    });

    Sentry.setContext("user_info", {
      fullName: userInfo.fullName,
      hasPhoneNumber: !!userInfo.phoneNumber,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Create a span for performance monitoring
   */
  static startPaymentSpan(operation: string, description?: string) {
    return Sentry.startSpan(
      {
        op: `payment.${operation}`,
        name: description || operation,
      },
      (span) => {
        span.setAttribute("component", "payment_flow");
        return span;
      },
    );
  }
}
